import type { FixtureConfigSlice } from '@/features/tournament-wizard/engine/types';
import {
  REGLAMENTO_HEADING,
  REGLAMENTO_PLACEHOLDER_TEXT,
} from '@/features/tournament-wizard/sports/shared/templates/basicInfoTemplates';
import {
  AmericanFinalBracketSize,
  AmericanGroupFormat,
  OverflowGroupClassification,
  TournamentFormat,
} from '@/types/tournament.model';
import { resolveAmericanGroupFormat } from '@/utils/tournament-format';
import {
  hasConfigurableGroupStage,
  resolveOverflowGroupClassificationForApi,
} from '@/utils/tournament-overflow-group';

/**
 * The description is written in the first wizard step, before the organizer has
 * picked the tournament format. The template plants a placeholder paragraph
 * there; these helpers swap it for the implicit rules of the chosen format.
 */

/** The config the rules come from: the wizard slice, or a saved tournament. */
export interface ReglamentoSource
  extends Pick<
    FixtureConfigSlice,
    | 'fixtureType'
    | 'teamsPerGroups'
    | 'classifiedPerGroups'
    | 'overflowGroupClassification'
    | 'americanFinalBracketSize'
    | 'americanGroupFormat'
  > {
  /** Read as SHORT when missing, the same fallback the wizard sends to the API. */
  format?: TournamentFormat;
}

type ResolvedSource = ReglamentoSource & { format: TournamentFormat };

/** Same defaults the fixture step starts Corto with. */
const DEFAULT_TEAMS_PER_GROUP = 3;
const DEFAULT_CLASSIFIED_PER_GROUP = 2;

function reglamentoParagraph(body: string): string {
  return `<p>${REGLAMENTO_HEADING} ${body}</p>`;
}

/**
 * The classification option decides both halves of the group of 4, as the
 * backend's fixture does: the extra qualifier pairs winners and losers
 * together, otherwise each initial winner crosses with the other match's loser.
 */
function buildShortReglamento(source: ResolvedSource): string {
  const teamsPerGroup = source.teamsPerGroups ?? DEFAULT_TEAMS_PER_GROUP;
  const classifiedPerGroup =
    source.classifiedPerGroups ?? DEFAULT_CLASSIFIED_PER_GROUP;
  const hasExtraClassifier =
    resolveOverflowGroupClassificationForApi(source) ===
    OverflowGroupClassification.EXTRA_CLASSIFIER;
  const secondRound = hasExtraClassifier
    ? 'ganador vs ganador y perdedor vs perdedor'
    : 'el ganador de cada partido juega contra el perdedor del otro';
  const overflowClassification = hasExtraClassifier
    ? `clasifican ${classifiedPerGroup + 1}`
    : `también clasifican ${classifiedPerGroup}`;

  return reglamentoParagraph(
    `Los grupos son de ${teamsPerGroup} equipos y clasifican ${classifiedPerGroup}. ` +
      `Si por la cantidad de inscriptos se arma un grupo de ${teamsPerGroup + 1}, ` +
      `se juegan 2 partidos iniciales y luego ${secondRound}; ` +
      `en ese grupo ${overflowClassification}.`,
  );
}

const AMERICAN_BRACKET_RULES: Record<AmericanFinalBracketSize, string> = {
  [AmericanFinalBracketSize.NONE]:
    'No hay cuadro final: el torneo se define en la fase de grupos.',
  [AmericanFinalBracketSize.FINAL]:
    'Los mejores de cada grupo pasan a la final; los lugares se reparten entre los grupos.',
  [AmericanFinalBracketSize.FOUR]:
    'Los mejores de cada grupo pasan a un cuadro final de 4; los lugares se reparten entre los grupos.',
  [AmericanFinalBracketSize.EIGHT]:
    'Los mejores de cada grupo pasan a un cuadro final de 8; los lugares se reparten entre los grupos.',
};

/**
 * No numbers here: the backend sizes Americano groups from the registrations
 * (ignoring `teamsPerGroups`) and splits the final bracket's places among
 * however many groups that yields, and neither is known at creation time.
 */
function buildAmericanReglamento(source: ResolvedSource): string {
  const groupMatches =
    resolveAmericanGroupFormat(source) === AmericanGroupFormat.WINNER_LOSER
      ? 'En los grupos de 4 se juegan 2 partidos iniciales y luego ganador vs ganador ' +
        'y perdedor vs perdedor; en los demás, todos contra todos.'
      : 'Dentro de cada grupo se juega todos contra todos.';
  const bracket =
    AMERICAN_BRACKET_RULES[
      source.americanFinalBracketSize ?? AmericanFinalBracketSize.NONE
    ];

  return reglamentoParagraph(
    `Los grupos se arman según la cantidad de inscriptos. ${groupMatches} ${bracket}`,
  );
}

/**
 * The rules implied by the format, or null when it implies none (Solo
 * Eliminatorias has no groups; LONG is configured elsewhere).
 */
function buildReglamentoHtml(
  source: ReglamentoSource | null | undefined,
): string | null {
  if (!source) return null;
  const config = { ...source, format: source.format ?? TournamentFormat.SHORT };
  if (!hasConfigurableGroupStage(config)) return null;

  return config.format === TournamentFormat.AMERICAN
    ? buildAmericanReglamento(config)
    : buildShortReglamento(config);
}

/**
 * DOMParser builds an inert document: unlike `innerHTML` on an element of the
 * live one, it never fetches images or runs inline handlers, and the
 * description can come from the server.
 */
function parseHtml(html: string): Document {
  return new DOMParser().parseFromString(html, 'text/html');
}

function findPlaceholders(doc: Document): HTMLParagraphElement[] {
  return Array.from(doc.querySelectorAll('p')).filter(
    (p) =>
      (p.textContent ?? '').replace(/\s+/g, ' ').trim() ===
      REGLAMENTO_PLACEHOLDER_TEXT,
  );
}

export function hasReglamentoPlaceholder(html: string | undefined): boolean {
  return !!html && findPlaceholders(parseHtml(html)).length > 0;
}

/**
 * Replaces the template's reglamento placeholder with the rules implied by the
 * tournament config, and drops it when the format implies none. A pasted
 * duplicate is dropped too, so a published tournament never reads "se
 * completará automáticamente". Without a placeholder the description is
 * returned untouched.
 */
export function resolveDescriptionReglamento(
  html: string,
  source: ReglamentoSource | null | undefined,
): string {
  if (!html) return html;

  const doc = parseHtml(html);
  const [placeholder, ...duplicates] = findPlaceholders(doc);
  if (!placeholder) return html;

  duplicates.forEach((p) => p.remove());
  const reglamento = buildReglamentoHtml(source);
  if (reglamento === null) {
    placeholder.remove();
  } else {
    placeholder.outerHTML = reglamento;
  }

  return doc.body.innerHTML;
}
